import React, {useState} from 'react';

import {SerializedError} from '@reduxjs/toolkit';
import {FetchBaseQueryError} from '@reduxjs/toolkit/query';
import cs from 'classnames';

import StateButton from 'app/general/widgets/StateButton';
import {useUpdateTeam} from 'app/teams/api';
import {gt} from 'app/utils/i18n';

interface Props {
  teamId: number;
  name: string;
  description: string;
  teammateCanAssign: boolean;
  handleClose: () => void;
}

export function EditTeamNameAndDescription({
  teamId,
  handleClose,
  ...props
}: Props) {
  const [name, setName] = useState(props.name);
  const [description, setDescription] = useState(props.description);
  const [teammateCanAssign, setTeammateCanAssign] = useState(
    props.teammateCanAssign
  );

  const [isBusy, setIsBusy] = useState(false);
  const nameFieldRef = React.useRef<HTMLInputElement | null>(null);

  const [updateTeam, {error}] = useUpdateTeam();
  const formErrors = extractFormErrors(error);

  React.useEffect(() => {
    const nameField = nameFieldRef.current;

    if (nameField != null) {
      nameField.focus();
      nameField.setSelectionRange(0, nameField.value.length);
    }
  }, []);

  const closeEditMode = () => {
    setIsBusy(false);
    handleClose();
  };

  const updateTeamDetails = (
    name: string,
    description: string,
    teammates_can_assign_task: boolean
  ) => {
    setIsBusy(true);

    updateTeam({
      teamid: teamId,
      body: {
        name,
        description,
        teammates_can_assign_task,
      },
    })
      .unwrap()
      .then(closeEditMode, () => setIsBusy(false));
  };

  return (
    <div className="mt-3">
      <div className="form-group">
        <input
          type="text"
          autoComplete="off"
          placeholder={gt('Team name')}
          onChange={(e) => setName(e.target.value)}
          value={name}
          className={cs('form-control form-control-lg', {
            'is-invalid': formErrors['name'],
          })}
          ref={nameFieldRef}
        />
        <ErrorMessage message={formErrors['name']} />
      </div>
      <div className="form-group">
        <input
          type="text"
          className={cs('form-control', {
            'is-invalid': formErrors['description'],
          })}
          maxLength={512}
          autoComplete="off"
          placeholder={gt('Enter a description for this team')}
          onChange={(e) => setDescription(e.target.value)}
          value={description}
        />
        <ErrorMessage message={formErrors['description']} />
      </div>
      <div className="form-group">
        <div className="form-check">
          <input
            type="checkbox"
            className={cs('form-check-input', {
              'is-invalid': formErrors['teammates_can_assign_task'],
            })}
            id="teammateCanAssignCheckbox"
            placeholder={gt(
              'Teammates can assign tasks based on procedures and processes attached to this team'
            )}
            onChange={(e) => setTeammateCanAssign(e.target.checked)}
            checked={teammateCanAssign}
          />
          <label
            htmlFor="teammateCanAssignCheckbox"
            className="form-check-label"
          >
            {gt('Teammates can assign tasks')}
          </label>
          <ErrorMessage message={formErrors['teammates_can_assign_task']} />
        </div>
      </div>
      <div className="d-flex justify-content-end">
        <button
          disabled={isBusy}
          className="btn btn-secondary mr-2"
          onClick={handleClose}
        >
          {gt('Cancel')}
        </button>{' '}
        <StateButton
          className="btn btn-primary"
          isIdle={!isBusy}
          busyText={gt('Saving...')}
          onClick={() =>
            updateTeamDetails(name, description, teammateCanAssign)
          }
        >
          {gt('Save')}
        </StateButton>
      </div>
    </div>
  );
}

function extractFormErrors(
  error: SerializedError | FetchBaseQueryError | undefined
): {[field: string]: string} {
  let errors: {[field: string]: string} = {};

  if (error != null && 'status' in error && error.status === 400) {
    for (const field of Object.keys(error.data as any)) {
      // capture field first error only
      errors[field] = (error.data as any)[field][0];
    }
  }
  return errors;
}

function ErrorMessage({message}: {message?: string}): null | JSX.Element {
  return message == null ? null : (
    <div className="invalid-feedback">{message}</div>
  );
}
